import { getPlayers } from "@/api/publicRequests/players/getPlayers";
import { Button } from "@/design-system/Button/Button";
import Input from "@/design-system/Input/Input";
import {
  PlayerCardWithMetaRating,
  PlayerCardWithMetaRatingSkeleton,
} from "@/design-system/PlayerCard/PlayerCard";
import BackButton from "@/design-system/SpecificButtons/BackButton/BackButton";
import {
  COMPARE_SECTION,
  CompareSubsection,
  trackAddedPlayerToCompare,
} from "@/events/compareMixpanel";
import { convertPlayerPrice } from "@/functions/convertPlayerPrice";
import useDebounce from "@/hooks/useDebounce";
import {
  MetaRatingsPlayer,
  playerRoleLabelSelector,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import {
  addComparePlayer,
  comparePlayerKey,
  comparePlayersSelector,
  fetchSuggestedComparePlayers,
  suggestedComparePlayersSelector,
  suggestionsLoadingSelector,
  suggestionsQuerySelector,
} from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const MIN_SEARCH_LENGTH = 3;
/** Placeholder cards while a fetch is in flight (~2 rows on most widths). */
const SKELETON_COUNT = 10;

/**
 * Search-and-pick view shown inside the compare bottom sheet when the empty
 * add-slot is clicked. Same backend search as the Players database
 * (`/players?…&search=`), debounced with the shared min-length gate. Clicking
 * a result adds it to the compare slice and calls `onAdded` to switch back.
 */
export const AddPlayerSearch = ({
  onAdded,
  onCancel,
  playerRoleId,
  subsection,
}: {
  onAdded: () => void;
  /** Back to the comparison without adding a player. */
  onCancel: () => void;
  /** Role the comparison is graded under — narrows suggestions to that role. */
  playerRoleId?: number;
  /** Which host this view is open in — stamped on the compare event. */
  subsection: CompareSubsection;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("compare");
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm, errorMessage } = useDebounce(searchTerm, {
    minLength: MIN_SEARCH_LENGTH,
    contextForErrorMessage: t("search.playerNameContext"),
  });
  const [players, setPlayers] = useState<MetaRatingsPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const comparedPlayers = useSelector(comparePlayersSelector);
  const suggestedPlayers = useSelector(suggestedComparePlayersSelector);
  const suggestionsQuery = useSelector(suggestionsQuerySelector);
  const suggestionsLoading = useSelector(suggestionsLoadingSelector);
  const suggestedRoleLabel = useSelector(
    playerRoleLabelSelector({
      playerRoleId: suggestionsQuery?.playerRoleId ?? -1,
    }),
  );

  // Suggestions for the current compared set (price of the most expensive
  // compared player or cheaper, graded under the selected role or the first
  // player's top-3) — thunk no-ops when the stored ones already match, so
  // reopening the view doesn't refetch.
  useEffect(() => {
    dispatch(fetchSuggestedComparePlayers({ playerRoleId }));
  }, [dispatch, playerRoleId]);

  useEffect(() => {
    if (debouncedSearchTerm.length < MIN_SEARCH_LENGTH) {
      setPlayers([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPlayers({
      page: 1,
      sort: "meta-rating",
      searchText: debouncedSearchTerm,
    })
      .then(({ players }) => {
        if (!cancelled) setPlayers(players);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm]);

  // Search takes over as soon as it produces results; until then (view just
  // opened, or the term is below the min length) show the suggestions,
  // minus anyone compared since they were fetched.
  const showSuggestions = debouncedSearchTerm.length < MIN_SEARCH_LENGTH;
  // Any version of a compared player is filtered out of the suggestions (a
  // base card next to its compared evo is noise)…
  const comparedIds = new Set(comparedPlayers.map((p) => p.resourceId));
  // …but the dedup (and so the tracking guard) runs on the full compare
  // identity: with only an evo compared, its base card is still addable.
  const comparedKeys = new Set(comparedPlayers.map(comparePlayerKey));

  const addPlayer = (player: DetailedPlayer) => {
    // MetaRatingsPlayer is a superset of Player. Deep copy: Immer freezes
    // dispatched payloads, and the arrays are shared with the results state.
    dispatch(addComparePlayer(structuredClone(player)));
    // Suggestions are pre-filtered by compared id, but search results are not —
    // re-picking a staged player is a no-op in the slice (it dedups), so firing
    // here would report a player_count the comparison never reached.
    if (!comparedKeys.has(comparePlayerKey(player))) {
      trackAddedPlayerToCompare({
        origin: { section: COMPARE_SECTION, subsection },
        source: showSuggestions ? "suggestion" : "search",
        player_count: comparedPlayers.length + 1,
      });
    }
    onAdded();
  };
  const visiblePlayers = showSuggestions
    ? suggestedPlayers.filter((p) => !comparedIds.has(p.resourceId))
    : players;
  // Skeletons whenever the visible list is being fetched — stale suggestions
  // (or search results) never show as if they were the fresh ones.
  const showSkeletons = showSuggestions ? suggestionsLoading : loading;
  const showSuggestionsHeading =
    showSuggestions &&
    !suggestionsLoading &&
    visiblePlayers.length > 0 &&
    suggestionsQuery !== null;

  // Plain-text description of the suggestions query: the selected role written
  // out when one was sent (e.g. "Poacher ST"), plain "similar" when the first
  // player's best role was used, plus the price cap when one applied. An
  // unfiltered query (nothing compared) is just the overall best players.
  const maxPrice = suggestionsQuery?.maxPrice
    ? convertPlayerPrice(suggestionsQuery.maxPrice, true)
    : undefined;
  const suggestionsDescription = suggestionsQuery
    ? comparedPlayers.length === 0
      ? ""
      : suggestedRoleLabel
        ? maxPrice
          ? t("search.showingRolePlayersUnder", {
              role: suggestedRoleLabel,
              price: maxPrice,
            })
          : t("search.showingRolePlayers", { role: suggestedRoleLabel })
        : maxPrice
          ? t("search.showingSimilarPlayersUnder", { price: maxPrice })
          : t("search.showingSimilarPlayers")
    : "";

  return (
    <div className="mx-auto w-full max-w-content px-4 pt-2 pb-8 large:px-16 touch-device:px-4">
      <div className="flex w-full items-center gap-x-2">
        <BackButton
          onClick={onCancel}
          aria-label={t("search.backToComparison")}
        />
        <div className="w-full max-w-90">
          <Input
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t("search.placeholder")}
            type="text"
            size="small"
            autoFocus
          />
        </div>
      </div>
      {errorMessage && (
        <p className="pt-2 text-sm text-dark-secondary">{errorMessage}</p>
      )}
      {!errorMessage && !loading && searchTerm && players.length === 0 && (
        <p className="pt-2 text-sm text-dark-secondary">
          {debouncedSearchTerm.length >= MIN_SEARCH_LENGTH && (
            <span>{t("search.noPlayersFound")}</span>
          )}
        </p>
      )}
      {showSuggestionsHeading && (
        <p className="pt-6 text-sm text-dark-secondary">
          {suggestionsDescription}
        </p>
      )}
      {/* Front-page grid system (PLAYER_GRID): fixed gap and a fixed column
          count per breakpoint — the cards' boxes stretch to the cell
          (`fullWidth`) while the card art inside stays its fixed size. */}
      <div
        className={`grid grid-cols-2 gap-2 min-[640px]:grid-cols-3 min-[960px]:grid-cols-5 min-[1120px]:grid-cols-6 min-[1400px]:grid-cols-7 ${
          showSuggestionsHeading ? "pt-3" : "pt-6"
        }`}
      >
        {showSkeletons
          ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
              <PlayerCardWithMetaRatingSkeleton
                key={i}
                scale={0.9}
                fullWidth
                hideLabel
              />
            ))
          : visiblePlayers.map((player) => (
              <button
                key={player.resourceId}
                type="button"
                onClick={() => addPlayer(player as unknown as DetailedPlayer)}
                className="w-full cursor-pointer"
                aria-label={t("add.addNamed", { name: player.cardName })}
              >
                <PlayerCardWithMetaRating
                  player={player}
                  scale={0.9}
                  fullWidth
                  hideLabel
                  hideNewTag
                />
              </button>
            ))}
      </div>
    </div>
  );
};
